
'use client';

import * as React from 'react';

type Theme = 'light' | 'dark';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
};

const initialState: ThemeProviderState = {
  theme: 'light',
  setTheme: () => null,
  resolvedTheme: 'light',
};

const ThemeProviderContext = React.createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'light', // Explicitly set default to light
  storageKey = 'everhouse-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<Theme>(
    () => {
      // Check if we're in the browser environment
      if (typeof window !== 'undefined') {
        const storedTheme = localStorage.getItem(storageKey) as Theme;
        // Only accept 'light' or 'dark' as valid themes from storage
        return (storedTheme === 'light' || storedTheme === 'dark') 
          ? storedTheme 
          : 'light'; // Always default to light if no valid theme is stored
      }
      return 'light'; // Explicitly return light as default
    }
  );
  
  // Since we removed system, resolvedTheme is always the same as theme
  const resolvedTheme = theme;

  React.useEffect(() => {
    // Check if we're in the browser environment
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    
    // Remove previous theme class
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(theme);
    
    // Save theme preference to localStorage
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  const value = {
    theme,
    resolvedTheme,
    setTheme: (newTheme: Theme) => {
      setTheme(newTheme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = React.useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
